import React, { useEffect, useState } from 'react';
import adminService from '../../services/adminService';
import { ApplicationEvaluationsResponseDto } from '../../types/admin';
import Alert from 'shared/components/UC/Alert';
import Badge from 'shared/components/UC/Badge';
import Button from 'shared/components/UC/Button';
import Modal from 'shared/components/UC/Modal';
import styles from './EvaluationsModal.module.css';

interface EvaluationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId: string;
  applicationNumber: string;
}

const EvaluationsModal: React.FC<EvaluationsModalProps> = ({
  isOpen,
  onClose,
  applicationId,
  applicationNumber,
}) => {
  const [evaluationData, setEvaluationData] = useState<ApplicationEvaluationsResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOptedOut, setShowOptedOut] = useState(false);

  useEffect(() => {
    if (isOpen && applicationId) {
      loadEvaluationData();
    }
  }, [isOpen, applicationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvaluationData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getApplicationEvaluations(applicationId);
      setEvaluationData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar las evaluaciones');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatScore = (score: number | null) => {
    return score !== null ? score.toFixed(2) : 'N/A';
  };

  const statusConfig: Record<string, { label: string; variant: 'success' | 'secondary' | 'info' | 'danger' | 'warning' }> = {
    completed:            { label: 'Evaluado',            variant: 'success'   },
    pending:              { label: 'Sin respuesta',       variant: 'secondary' },
    accepted_not_started: { label: 'Sin iniciar',         variant: 'secondary' },
    in_progress:          { label: 'En evaluación',       variant: 'info'      },
    declined:             { label: 'Rechazada',           variant: 'danger'    },
    conflict_declared:    { label: 'Conflicto declarado', variant: 'warning'   },
  };

  const getStatusBadge = (status: string) => {
    const { label, variant } = statusConfig[status] ?? { label: 'Sin respuesta', variant: 'secondary' as const };
    return <Badge label={label} variant={variant} />;
  };

  const criteriaLabels = {
    originality_risk: 'Originalidad y Riesgo',
    national_relevance: 'Relevancia Nacional',
    global_scalability: 'Escalabilidad Global',
    international_collaboration: 'Colaboración Internacional',
    technical_viability: 'Viabilidad Técnica',
    results_quality: 'Calidad de Resultados',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Evaluaciones de la Postulación"
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      <p className={styles.applicationNumber}>Folio: {applicationNumber}</p>

      {loading && (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Cargando evaluaciones...</p>
        </div>
      )}

      {error && (
        <div className={styles.error}>
          <p>{error}</p>
          <Button variant="primary" onClick={loadEvaluationData}>
            Reintentar
          </Button>
        </div>
      )}

      {evaluationData && !loading && !error && (
        <>
          {/* Summary Section */}
          <div className={styles.summarySection}>
            <h3>Resumen de Evaluaciones</h3>
            <div className={styles.summaryCards}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryNumber}>{evaluationData.summary.numberOfEvaluations}</div>
                <div className={styles.summaryLabel}>Evaluaciones</div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryNumber}>{evaluationData.summary.numberOfEvaluators}</div>
                <div className={styles.summaryLabel}>Evaluadores</div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryNumber}>
                  {formatScore(evaluationData.summary.weightedScoreDistribution.average)}
                </div>
                <div className={styles.summaryLabel}>Puntaje Promedio</div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryNumber}>{evaluationData.summary.completionPercentage}%</div>
                <div className={styles.summaryLabel}>Completado</div>
              </div>
            </div>
          </div>
          {/* Deviation callout — only when arbitration threshold exceeded */}
          {(() => {
            const scores = evaluationData.evaluations
              .filter((e) => e.status === 'submitted' && e.finalScore !== null)
              .map((e) => e.finalScore as number);
            if (scores.length < 2) return null;
            const deviation = Math.max(...scores) - Math.min(...scores);
            if (deviation <= evaluationData.maximumScoreDifference) return null;
            return (
              <Alert
                variant="error"
                title={`Desviación entre evaluadores: ${deviation.toFixed(2)}`}
              >
                Supera el umbral de {evaluationData.maximumScoreDifference.toFixed(1)}. Se requiere árbitro.
              </Alert>
            );
          })()}

          {/* Individual Evaluations — only active evaluators */}
          {(() => {
            const optedOutStatuses = ['declined', 'conflict_declared'];
            const active = evaluationData.evaluations.filter(
              (e) => !optedOutStatuses.includes(e.assignmentStatus)
            );
            const optedOut = evaluationData.evaluations.filter(
              (e) => optedOutStatuses.includes(e.assignmentStatus)
            );
            return (
              <div className={styles.evaluationsSection}>
                <h3>Evaluaciones Individuales</h3>
                {active.length === 0 ? (
                  <p className={styles.noEvaluations}>No hay evaluaciones disponibles.</p>
                ) : (
                  <div className={styles.evaluationsTable}>
                    <table>
                      <thead>
                        <tr>
                          <th>Evaluador</th>
                          <th>Estado</th>
                          <th>Puntaje Final</th>
                          <th>Última Actualización</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {active.map((evaluation) => (
                          <tr key={evaluation.id}>
                            <td className={styles.evaluatorName}>
                              {evaluation.evaluatorName}
                              {evaluation.isArbiter && (
                                <Badge label="Árbitro" variant="warning" />
                              )}
                            </td>
                            <td>{getStatusBadge(evaluation.assignmentStatus)}</td>
                            <td className={styles.score}>{formatScore(evaluation.finalScore)}</td>
                            <td className={styles.date}>{formatDate(evaluation.updatedAt)}</td>
                            <td>
                              {evaluation.status === 'submitted' && (
                                <a
                                  href={`/admin/evaluation/${evaluation.id}`}
                                  rel="noopener noreferrer"
                                  className={styles.viewLink}
                                >
                                  Ver Evaluación
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {optedOut.length > 0 && (
                  <div className={styles.optedOutSection}>
                    <button
                      className={styles.optedOutToggle}
                      onClick={() => setShowOptedOut((v) => !v)}
                      aria-expanded={showOptedOut}
                    >
                      <i className={`uc-icon icon-size--sm`}>
                        {showOptedOut ? 'expand_less' : 'expand_more'}
                      </i>
                      {showOptedOut ? 'Ocultar' : 'Ver'} evaluadores que no participaron ({optedOut.length})
                    </button>
                    {showOptedOut && (
                      <ul className={styles.optedOutList}>
                        {optedOut.map((e) => (
                          <li key={e.id} className={styles.optedOutItem}>
                            <span className={styles.optedOutName}>{e.evaluatorName}</span>
                            {getStatusBadge(e.assignmentStatus)}
                            {e.conflictDetails && (
                              <span className={styles.optedOutDetail}>{e.conflictDetails}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Criteria Distribution */}
          <div className={styles.criteriaSection}>
            <h3>Distribución por Criterios</h3>
            <div className={styles.criteriaGrid}>
              {Object.entries(evaluationData.summary.criteriaDistribution).map(([criterion, stats]) => {
                const range = stats.max - stats.min;
                const isHighRange = range > (evaluationData.maximumCriteriaScoreDifference || 2);

                return (
                  <div key={criterion} className={styles.criteriaCard}>
                    <h4>{criteriaLabels[criterion as keyof typeof criteriaLabels]}</h4>
                    <div className={styles.criteriaStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Promedio:</span>
                        <span className={styles.statValue}>{formatScore(stats.average)}</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Rango:</span>
                        <span className={styles.statValue}>
                          {formatScore(stats.min)} - {formatScore(stats.max)}
                        </span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={styles.statLabel}>Diferencia máxima:</span>
                        <span className={`${styles.statValue} ${isHighRange ? styles.highRange : ''}`}>
                          {formatScore(range)}
                        </span>
                      </div>
                      {evaluationData.evaluations
                        .filter(evaluation => evaluation.status === 'submitted')
                        .map((evaluation) => (
                          <div key={evaluation.id} className={styles.statItem}>
                            <span className={styles.statLabel}>{evaluation.evaluatorName}:</span>
                            <span className={styles.statValue}>
                              {formatScore(evaluation.criteriaScores[criterion as keyof typeof evaluation.criteriaScores])}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default EvaluationsModal;
